// FleetBackfill.jsx - Acompanhamento ao vivo do backfill ADN da frota.
// Consome os endpoints que já existem no backend (GET/POST /downloads/adn/backfill*)
// por polling — não há SSE/WebSocket no projeto e a granularidade de segundos
// basta para um run que dura de minutos a horas.
//
// ⚠️ O estado do run vive em memória no backend (adnFleetBackfillService). Se o
// processo reinicia no meio, o status cai para o relatório em disco com
// `source: "arquivo"` e `interrupted: true` — a tela sinaliza isso e orienta o
// re-disparo com "retomar", porque o run NÃO continua sozinho.
import { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "@astryxdesign/core/Card";
import { Stack, HStack } from "@astryxdesign/core/Stack";
import { Text, Heading } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { Badge } from "@astryxdesign/core/Badge";
import { Banner } from "@astryxdesign/core/Banner";
import { Spinner } from "@astryxdesign/core/Spinner";
import { ProgressBar } from "@astryxdesign/core/ProgressBar";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Selector } from "@astryxdesign/core/Selector";
import { Divider } from "@astryxdesign/core/Divider";
import { Table, proportional, pixel } from "@astryxdesign/core/Table";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { downloadsAPI, handleAPIError } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import "../../fontawesome";

// Rodando: precisa de resposta rápida. Ocioso: só para perceber um run
// disparado por outra pessoa (ou pelo scheduler) sem martelar a API.
const POLL_RUNNING_MS = 5000;
const POLL_IDLE_MS = 30000;

const MODE_OPTIONS = [
  { value: "incremental", label: "Incremental (do último NSU)" },
  { value: "reprocess", label: "Reprocessar (zera o checkpoint)" },
];

const CONCURRENCY_OPTIONS = [4, 6, 8, 10, 12, 16].map((n) => ({
  value: String(n),
  label: `${n} simultâneas`,
}));

const RESUME_OPTIONS = [
  { value: "true", label: "Retomar (pula as já limpas)" },
  { value: "false", label: "Do zero (reprocessa todas)" },
];

/** "1h 04min", "3min 12s", "45s" — duração humana a partir de segundos. */
const formatDuration = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "—";
  const s = Math.round(totalSeconds);
  if (s < 60) return `${s}s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min}min ${String(s % 60).padStart(2, "0")}s`;
  const h = Math.floor(min / 60);
  return `${h}h ${String(min % 60).padStart(2, "0")}min`;
};

const formatDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR") : "—";

/**
 * O snapshot não expõe `totalToProcess`; ele é derivado.
 * - source "memoria": a meta do run é `totalEligible - skippedCleanOnResume`
 *   (as puladas no resume nunca entram na fila).
 * - source "arquivo": só sobrou o relatório, cujo doneCount é o nº de empresas
 *   com resultado gravado — a régua ali é o total de elegíveis.
 */
const deriveProgress = (status) => {
  if (!status || !status.source) return null;
  const done = status.doneCount || 0;
  const total =
    status.source === "memoria"
      ? Math.max(
          (status.totalEligible || 0) - (status.skippedCleanOnResume || 0),
          0,
        )
      : status.totalEligible || 0;
  const percentage = total > 0 ? Math.round((done / total) * 100) : 0;
  return { done, total, percentage };
};

/** ETA pela vazão média do run (empresas/segundo desde o startedAt). */
const estimateRemaining = (status, progress) => {
  if (!status?.running || !progress || progress.total <= 0) return null;
  if (!status.startedAt || progress.done <= 0) return null;
  const elapsed = (Date.now() - new Date(status.startedAt).getTime()) / 1000;
  if (elapsed <= 0) return null;
  const remaining = progress.total - progress.done;
  if (remaining <= 0) return null;
  return (elapsed / progress.done) * remaining;
};

const statusBadge = (status) => {
  if (!status || !status.source)
    return <Badge variant="neutral" label="Nunca executado" />;
  if (status.running && status.stopRequested)
    return <Badge variant="warning" label="Parando..." />;
  if (status.running) return <Badge variant="info" label="Em execução" />;
  if (status.interrupted)
    return <Badge variant="warning" label="Interrompido" />;
  return <Badge variant="success" label="Concluído" />;
};

/**
 * Bloco compacto de número + rótulo usado na régua de totais.
 * `alertVariant` destaca o número como Badge quando ele é > 0 (contadores de
 * problema só devem chamar atenção quando existe problema).
 */
function Stat({ label, value, alertVariant }) {
  return (
    <Stack gap={0}>
      {alertVariant && value > 0 ? (
        <Badge variant={alertVariant} label={String(value)} />
      ) : (
        <Text size="lg" weight="semibold">
          {value}
        </Text>
      )}
      <Text type="supporting">{label}</Text>
    </Stack>
  );
}

export default function FleetBackfill() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [mode, setMode] = useState("incremental");
  const [concurrency, setConcurrency] = useState("10");
  const [resume, setResume] = useState("true");

  const intervalRef = useRef(null);
  const inFlightRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    // Um tick lento não pode empilhar requisições em cima do anterior.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const response = await downloadsAPI.getAdnFleetBackfillStatus();
      if (response.success) {
        setStatus(response.data);
        setError(null);
      }
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  const running = Boolean(status?.running);

  // Polling: cadência depende de estar rodando; aba oculta não consulta.
  useEffect(() => {
    fetchStatus();
    const tick = () => {
      if (document.hidden) return;
      fetchStatus();
    };
    intervalRef.current = setInterval(
      tick,
      running ? POLL_RUNNING_MS : POLL_IDLE_MS,
    );
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, fetchStatus]);

  // Ao voltar para a aba, atualiza na hora em vez de esperar o próximo tick.
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) fetchStatus();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () =>
      document.removeEventListener("visibilitychange", onVisibility);
  }, [fetchStatus]);

  const handleStart = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await downloadsAPI.startAdnFleetBackfill({
        mode,
        concurrency: parseInt(concurrency, 10),
        resume: resume === "true",
      });
      setShowStartDialog(false);
      await fetchStatus();
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    const ok = window.confirm(
      "Parar o backfill da frota? A fila é esvaziada e as coletas em andamento vão concluir. Depois é possível retomar de onde parou.",
    );
    if (!ok) return;
    try {
      setActionLoading(true);
      setError(null);
      await downloadsAPI.stopAdnFleetBackfill();
      await fetchStatus();
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const progress = deriveProgress(status);
  const eta = estimateRemaining(status, progress);
  const totals = status?.totals || {};
  const review = status?.companiesToReview || [];

  const reviewColumns = [
    {
      key: "label",
      header: "Empresa",
      width: proportional(2),
      renderCell: (r) => <Text size="sm">{r.label}</Text>,
    },
    {
      key: "motivo",
      header: "Motivo",
      width: proportional(3),
      renderCell: (r) => (
        <Text size="sm" type="supporting">
          {r.motivo}
        </Text>
      ),
    },
  ];

  const activeColumns = [
    {
      key: "label",
      header: "Empresa",
      width: proportional(3),
      renderCell: (a) => <Text size="sm">{a.label}</Text>,
    },
    {
      key: "elapsedSeconds",
      header: "Tempo",
      width: pixel(120),
      renderCell: (a) => (
        <Text size="sm" type="supporting">
          {formatDuration(a.elapsedSeconds)}
        </Text>
      ),
    },
  ];

  if (loading && !status) {
    return (
      <Card padding={6}>
        <Stack gap={2} hAlign="center">
          <Spinner size="lg" label="Carregando" />
          <Text type="supporting">Carregando status da frota...</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap={4}>
      <Card padding={4}>
        <Stack gap={4}>
          {/* Cabeçalho + ações */}
          <HStack gap={3} vAlign="center" hAlign="between" wrap="wrap">
            <HStack gap={2} vAlign="center" wrap="wrap">
              <Heading level={2}>Coleta da Frota (ADN)</Heading>
              {statusBadge(status)}
              {status?.source === "arquivo" && (
                <Badge variant="neutral" label="Dados do relatório em disco" />
              )}
            </HStack>
            <HStack gap={2} vAlign="center" wrap="wrap">
              <Button
                label="Atualizar"
                variant="ghost"
                icon={<FontAwesomeIcon icon="sync" />}
                onClick={fetchStatus}
              />
              {isAdmin && running && (
                <Button
                  label="Parar"
                  variant="destructive"
                  icon={<FontAwesomeIcon icon="pause" />}
                  isDisabled={actionLoading || status?.stopRequested}
                  onClick={handleStop}
                />
              )}
              {isAdmin && !running && (
                <Button
                  label="Iniciar coleta"
                  variant="primary"
                  icon={<FontAwesomeIcon icon="play" />}
                  isDisabled={actionLoading}
                  onClick={() => setShowStartDialog(true)}
                />
              )}
            </HStack>
          </HStack>

          {error && <Banner status="error" title={error} />}

          {status?.interrupted && (
            <Banner
              status="warning"
              title="Último run interrompido"
              description="Provavelmente o backend reiniciou no meio. O run não retoma sozinho — inicie novamente com 'Retomar' para pular as empresas já coletadas."
            />
          )}

          {!status?.source ? (
            <EmptyState
              title="Nenhuma coleta da frota registrada"
              description="Nenhum backfill foi disparado desde o último boot do backend e não há relatório em disco."
              icon={<FontAwesomeIcon icon="satellite-dish" />}
            />
          ) : (
            <>
              {/* Progresso 100/800 */}
              <Stack gap={2}>
                <HStack gap={2} vAlign="center">
                  {running && <Spinner size="sm" label="Coletando" />}
                  <Text weight="semibold">
                    {progress.done} de {progress.total} empresas
                  </Text>
                  <Text type="supporting">({progress.percentage}%)</Text>
                </HStack>
                <ProgressBar
                  label="Progresso da coleta da frota"
                  isLabelHidden
                  value={Math.min(progress.percentage, 100)}
                  max={100}
                  variant={
                    running ? "accent" : status.interrupted ? "warning" : "success"
                  }
                />
                <HStack gap={4} hAlign="between" wrap="wrap">
                  <Text type="supporting">
                    Início: {formatDateTime(status.startedAt)}
                    {status.finishedAt
                      ? ` · Fim: ${formatDateTime(status.finishedAt)}`
                      : ""}
                  </Text>
                  <Text type="supporting">
                    {running && eta
                      ? `Restam ~${formatDuration(eta)}`
                      : running
                        ? "Calculando tempo restante..."
                        : "—"}
                  </Text>
                </HStack>
              </Stack>

              <Divider />

              {/* Régua de totais */}
              <HStack gap={6} wrap="wrap">
                <Stat label="Limpas" value={totals.limpas ?? 0} />
                <Stat
                  label="Com aviso/erro"
                  value={status.failedCount ?? review.length}
                  alertVariant="warning"
                />
                {status.source === "memoria" && (
                  <>
                    <Stat label="Na fila" value={status.pendingCount ?? 0} />
                    <Stat
                      label="Em andamento"
                      value={status.activeCount ?? 0}
                    />
                  </>
                )}
                <Stat label="Notas" value={totals.notas ?? 0} />
                <Stat label="Eventos" value={totals.eventos ?? 0} />
                <Stat
                  label="Erros de gravação"
                  value={totals.erros ?? 0}
                  alertVariant="warning"
                />
              </HStack>

              {/* Metadados do run */}
              <HStack gap={4} wrap="wrap">
                <Text type="supporting">
                  Modo: {status.mode === "reprocess" ? "Reprocessar" : "Incremental"}
                </Text>
                <Text type="supporting">
                  Concorrência: {status.concurrency ?? "—"}
                </Text>
                <Text type="supporting">
                  Elegíveis: {status.totalEligible ?? "—"}
                </Text>
                {status.skippedCleanOnResume > 0 && (
                  <Text type="supporting">
                    Puladas no retomar: {status.skippedCleanOnResume}
                  </Text>
                )}
                <Text type="supporting">
                  Disparado por: {status.requestedBy || "—"}
                </Text>
              </HStack>
            </>
          )}
        </Stack>
      </Card>

      {/* Empresas em execução neste momento */}
      {status?.active?.length > 0 && (
        <Card padding={4}>
          <Stack gap={3}>
            <Heading level={3}>Em andamento agora ({status.active.length})</Heading>
            <Table
              data={status.active}
              columns={activeColumns}
              idKey="companyId"
              density="compact"
            />
          </Stack>
        </Card>
      )}

      {/* Empresas que precisam de atenção */}
      {review.length > 0 && (
        <Card padding={4}>
          <Stack gap={3}>
            <Heading level={3}>Empresas para revisar ({review.length})</Heading>
            <Text type="supporting">
              Coletas que terminaram com aviso, erro ou truncadas — vale
              reprocessar ou investigar o certificado.
            </Text>
            <Table
              data={review.map((r, i) => ({ ...r, _id: `${r.label}-${i}` }))}
              columns={reviewColumns}
              idKey="_id"
              density="compact"
              hasHover
            />
          </Stack>
        </Card>
      )}

      {/* Diálogo de disparo */}
      {showStartDialog && (
        <Dialog
          isOpen
          onOpenChange={(open) => {
            if (!open && !actionLoading) setShowStartDialog(false);
          }}
          width={520}
        >
          <Stack gap={3}>
            <DialogHeader
              title="Iniciar coleta da frota"
              subtitle="Empresas ativas com download automático e certificado válido"
              onOpenChange={(open) => {
                if (!open && !actionLoading) setShowStartDialog(false);
              }}
              hasDivider
            />
            <Text type="supporting">
              Roda em segundo plano — esta tela acompanha o progresso.
            </Text>
            <Selector
              label="Modo"
              options={MODE_OPTIONS}
              value={mode}
              onChange={setMode}
            />
            <Selector
              label="Concorrência"
              options={CONCURRENCY_OPTIONS}
              value={concurrency}
              onChange={setConcurrency}
            />
            <Selector
              label="Ponto de partida"
              options={RESUME_OPTIONS}
              value={resume}
              onChange={setResume}
            />
            {mode === "reprocess" && (
              <Banner
                status="warning"
                title="Reprocessar é lento"
                description="Zera o checkpoint de NSU e re-varre a distribuição desde o piso. Não duplica notas (upsert por chave de acesso), mas leva bem mais tempo."
              />
            )}
            <HStack gap={2} hAlign="end">
              <Button
                label="Cancelar"
                variant="ghost"
                isDisabled={actionLoading}
                onClick={() => setShowStartDialog(false)}
              />
              <Button
                label={actionLoading ? "Disparando..." : "Iniciar"}
                variant="primary"
                isDisabled={actionLoading}
                onClick={handleStart}
              />
            </HStack>
          </Stack>
        </Dialog>
      )}
    </Stack>
  );
}
